import {QueryKey} from '@tanstack/react-query';
import {Database} from '@tnc/shared';

import {sortOptionT} from '@/lib/types/dataTable.types';
import {limitT} from '@/lib/types/pagination.types';
import {ColumnSelectionT, FilterT, SelectedColumnsKeysT} from '@/lib/types/query.types';
import {AllTablesT} from '@/lib/types/utility.types';

type TablesT = Database['public']['Tables'];
type ViewsT = Database['public']['Views'];
type FunctionsT = Database['public']['Functions'];

// Function to get the columns string
function getColumnsString<T extends keyof AllTablesT>(
  columns: string | number | symbol | ColumnSelectionT<T>[]
): string {
  if (typeof columns === 'string') return `[${columns}]`;
  if (typeof columns === 'number' || typeof columns === 'symbol') return `[${columns.toString()}]`;
  return `[${columns
    .map(col => {
      if (typeof col === 'string' && col.includes('(')) {
        const [relation, nestedCols] = col.split('(');
        const formattedNestedCols = nestedCols
          .slice(0, -1)
          .split(/,\s*/)
          .map(s => s.trim())
          .join(',');
        return `${relation}(${formattedNestedCols})`;
      }
      return col;
    })
    .join(',')}]`;
}

function getFiltersString<T extends keyof TablesT>(filters: FilterT<T>[]): string {
  if (filters.length === 0) return 'no filters';

  return filters
    .map(filter => {
      if (filter.not) {
        // It's a NotFilterT
        return `not ${getFiltersString([filter.not])}`;
      } else if (filter.or) {
        // It's an OrFilterT
        return `(${filter.or.map(f => getFiltersString([f])).join(' or ')})`;
      } else {
        // It's a BaseFilterT
        return `${String(filter.column)} ${filter.operator} ${filter.value}`;
      }
    })
    .join(' ');
}

export const getQueryKey = {
  // Stats View
  stats: <V extends keyof ViewsT>(statsView: V, enabled: boolean) => {
    const key: QueryKey = [statsView, enabled ? 'enabled' : 'disabled'];
    return key;
  },

  // Single Item
  detail: <T extends keyof TablesT>(table: T, id: number, columns: ColumnSelectionT<T>[], enabled: boolean) => {
    const queryKey: QueryKey = [table, 'detail', id, enabled, getColumnsString(columns)];
    return queryKey;
  },

  // All Items
  all: <T extends keyof TablesT, C extends ColumnSelectionT<T>[]>(
    table: T,
    params: {
      columns: C;
      enabled: boolean;
      filters: FilterT<T>[];
      sortOption: sortOptionT<T>;
      searchTerm: string;
      searchColumn: SelectedColumnsKeysT<T, C>;
    }
  ) => {
    const key: QueryKey = [
      table,
      'all',
      getColumnsString(params.columns),
      getFiltersString(params.filters),
      params.enabled ? 'enabled' : 'disabled',
      params.sortOption.column,
      params.sortOption.direction,
      params.searchTerm,
      params.searchColumn,
    ];
    return key;
  },

  // List of Items
  list: <T extends keyof TablesT, C extends ColumnSelectionT<T>[]>(
    table: T,
    params: {
      columns: C;
      enabled: boolean;
      sortOption: sortOptionT<T>;
      page: number;
      limit: limitT;
      searchTerm: string;
      searchColumn: SelectedColumnsKeysT<T, C>;
      filters: FilterT<T>[];
    }
  ) => {
    const key: QueryKey = [
      table,
      'list',
      getColumnsString(params.columns),
      params.enabled ? 'enabled' : 'disabled',
      getFiltersString(params.filters),
      params.sortOption.column,
      params.sortOption.direction,
      params.page,
      params.limit,
      params.searchTerm,
      params.searchColumn,
    ];
    return key;
  },

  // Functions
  function: <F extends keyof FunctionsT>(functionName: F, args: FunctionsT[F]['Args']) => {
    const key: QueryKey = [functionName, args];
    return key;
  },
};
